import { IgenomicsState } from '../../core/interfaces/genomics-state.interface';
import { createSelector } from '@ngrx/store';
import {
    getInputByKeyAndTimestamp,
    selectActiveJob,
    selectActiveWorkflow,
    selectRequestedRevision
} from '@ispm/workflow/store/workflow-config.selectors';
import { IWorkflowJobItem } from '@ispm/workflow/interfaces/job-item.interface';
import { IRevision } from '@ispm/workflow/interfaces/revision.interface';
import { IDefinition } from '../interfaces/definition.interface';
import { get, isEmpty } from 'lodash';
import { IWorkflow } from '@ispm/core/interfaces/workflow.interface';

export type UIConfigurationType = 'workflowVisibility' | 'workflowVisibility.barItems' | 'activeTabs' | 'targetStep' | 'workflowAction';

function getUiConfiguration(
  type: UIConfigurationType,
  workflow: IWorkflow,
  revision: IRevision,
  definition: IDefinition,
  property: null | string = 'uiOverride',
) {
  if (!revision || !workflow) {
    return;
  }

  const uiProperty = type ? `ui.${type}` : 'ui';
  let job = {}, input;
  const revisionUI = get(revision, uiProperty);

  if (['navigation complete', 'test completed'].includes(revision.status.toLowerCase())) {
    // todo: find more generic way to handle such cases - requires changes on workflow manager
    return (revisionUI && revision && revision.items && "activeTabs" === type) ? formatItems(revisionUI, revision.items) : revisionUI;
  }

  if (workflow.activeJob && workflow.activeJob.toLowerCase() !== 'none') {
    job = revision.items.find(item => item.id === workflow.activeJob);

    if (job) {
      input = getInputByKeyAndTimestamp((<IWorkflowJobItem>job).inputs, 'uiConfigurationOverride');
    }
  }

  if (isEmpty(job) && (workflow.activeJob && workflow.activeJob.toLowerCase() === 'none') && (!isEmpty(get(revision, 'reasonForStop.code')))) {
    job = revision.items.find(item => item.id === revision.activeJobId);
  }

  const configurationKey = input ? input.value : get(job, 'purpose', 'default');
  const overrideProperty = type ? `${property}.${type}` : `${property}`;
  const jobUI = get(job, uiProperty);
  const definitionUI = get(definition, `configurationsMap.${configurationKey}.${overrideProperty}`);

  return definitionUI || jobUI || revisionUI;
}

function formatItems(uiItems, revisionItems) {
  return uiItems.filter(function (uiItem) {
    if (uiItem.conditionPrevStepDone) {
      for (let revItem of revisionItems) {
        if (revItem.name == uiItem.conditionPrevStepDone) {
          if (revItem.status.toLowerCase() != "done") {
            return false;
          }

          break;
        }
      }
    }

    return true;
  });
}

function getUiStepOverrideConfiguration(
  job: IWorkflowJobItem,
  revision: IRevision,
  definition: IDefinition
) {
  if (!job) {
    return;
  }

  const configurationKey = job.purpose;

  return get(definition, `configurationsMap.${configurationKey}`);
}

export const selectConfigurationFeature = (state: IgenomicsState) => state.configuration.activeConfig;

export const selectConfigurationMap = (state: IgenomicsState) => state.configuration.definitions;

export const selectCurrentConfig = (resourceType: string) => createSelector(selectConfigurationFeature,
  ((activeConfig)  => {
     return activeConfig.get(resourceType) as IDefinition ;
     }));

export const selectConfigById = (resourceType: string, configId: string) => createSelector(selectConfigurationMap,
  ((configMap)  =>  {
    let config = configMap.get(resourceType);
    if(config) {
      return  config.get(configId) as IDefinition
    }
    else return null;
  }))

  export const selectCurrentConfigId = (resourceType: string) => createSelector(selectConfigurationFeature,
    activeConfig  => activeConfig.get(resourceType).id) ;


export const selectUiConfiguration = (key: UIConfigurationType, resType: string,  property: string = 'uiOverride',) => {
  return createSelector(
    [selectActiveWorkflow, selectRequestedRevision, selectCurrentConfig(resType)],
    (workflow, revision, definition)  => getUiConfiguration(key, workflow, revision, definition as IDefinition, property)
  )};


export const selectUiStepOverrideConfiguration = ( resType: string) => createSelector(
  [selectActiveJob, selectRequestedRevision, selectCurrentConfig(resType)], getUiStepOverrideConfiguration);



export const selectCurrentConfigPropertyValue = (resType: string, property: string) => {
  return createSelector(selectCurrentConfig(resType), resTypeConfiguration => resTypeConfiguration ? resTypeConfiguration[property] : null);
};


export const selectTargetStep = createSelector(
  [selectActiveJob, selectUiConfiguration('targetStep', 'Test', 'workflowAction')],
  (job, stepName) => stepName || job.name
);
